import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import type { AppType } from '@emma/api/src/presentation-v2/app';
import { EmmaApi, type EmmaEnv } from '@emma/api_client';
import { ApiError, isApiError } from '@emma/api_client/client';
import { useToast } from '@lego/klik-ui';
import { hc } from 'hono/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR, { type Key, type SWRConfiguration } from 'swr';
import useSWRMutation from 'swr/mutation';
import { UnexpectedError } from '@/errors/unexpected_error';
import { useAccessToken } from '@/hooks/use_access_token';
import { showToast } from '@/utility/ToastOptions';
import { ToastFactory } from '@/utility/toast_factory';

export type ApiCaller = <T>(fn: (api: EmmaApi) => Promise<T>) => Promise<T>;
export type ReadToastOptions = { showForbiddenErrorToast?: boolean };

/** Extract the `detail` string from an API error response, if present. */
export function getApiErrorDetail(error: unknown): string | undefined {
  if (!isApiError(error)) return undefined;
  const detail = (error.response?.data as { detail?: unknown })?.detail;
  return typeof detail === 'string' ? detail : undefined;
}

/** Extract a human-readable error message from an API error, including Zod validation details. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isApiError(error)) return fallback;
  const data = error.response?.data as
    | {
        detail?: string;
        errors?: { message?: string; path?: (string | number)[] }[];
      }
    | undefined;
  if (!data) return fallback;

  const errors = data.errors;
  if (errors?.length) {
    const messages = errors
      .map((e) => {
        const field = [...(e.path ?? [])].reverse().find((p): p is string => typeof p === 'string');
        const label = field
          ? field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())
          : '';
        return label ? `${label}: ${e.message}` : e.message;
      })
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }

  if (data.detail) return data.detail;
  return fallback;
}

/** Low-level: just gives you (fn)=>fn(api). Auth via MSAL or VITE_ACCESS_TOKEN. Caller handles caching + toasts. */
export function useCallApi(): ApiCaller {
  const getAccessToken = useAccessToken();

  return useCallback(
    async function callApi<T>(fn: (api: EmmaApi) => Promise<T>): Promise<T> {
      const token = await getAccessToken();

      const api = new EmmaApi(import.meta.env.VITE_ENV as EmmaEnv, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return await fn(api);
    },
    [getAccessToken]
  );
}

/**
 * High-level read: SWR + useCallApi. key=null pauses. Toast behavior can be configured per request.
 * API failures only surface a toast — never navigate the user away.
 */
export function useApiFetch<T>(
  key: Key | null,
  fetcher: (callApi: ApiCaller) => Promise<T>,
  options?: SWRConfiguration<T> & ReadToastOptions
) {
  const retryCount = 3;
  const retryBaseDelayMs = 500;
  const callApi = useCallApi();
  const toast = useToast();
  const { showForbiddenErrorToast = true, ...swrOptions } = options ?? {};

  const state = useSWR<T>(
    key,
    async () => {
      let attempt = 0;
      let error: unknown;
      while (attempt <= retryCount) {
        try {
          return await fetcher(callApi);
        } catch (e) {
          error = e;
          const isRetryable = isApiError(e);
          const status = isRetryable ? e.status : undefined;
          const retryableStatus = [500, 502, 503, 504, 522, 524, 408].includes(status ?? 0);
          const shouldRetry = attempt < retryCount && retryableStatus;

          if (shouldRetry) {
            console.warn(
              `API fetch failed with status ${status}. Retrying (${retryCount - attempt} retries left)...`
            );
            const delay = retryBaseDelayMs * 2 ** attempt;
            await new Promise((res) => setTimeout(res, delay));
            attempt += 1;
          } else {
            throw e;
          }
        }
      }
      throw error;
    },
    swrOptions
  );

  const previousErrorRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (key === null || state.isLoading) return;
    if (state.error && state.error !== previousErrorRef.current) {
      previousErrorRef.current = state.error;
      // Keep the user on the page when a read fails.
      if (showForbiddenErrorToast || !isApiError(state.error) || state.error.status !== 403) {
        showToast(toast, ToastFactory.fromError(state.error));
      }
    }
  }, [state.error, state.isLoading, key, toast, showForbiddenErrorToast]);

  return state;
}

/** High-level write: swr/mutation + useCallApi. Toasts on error and rejects the mutation. */
export function useApiMutation<T, A = void>(
  key: Key,
  mutator: (callApi: ApiCaller, arg: A) => Promise<T>,
  onError?: (error?: unknown) => void,
  suppressToast?: boolean
) {
  const callApi = useCallApi();
  const { trigger } = useSWRMutation<T, unknown, Key, A>(
    key,
    async (_key: Key, { arg }: { arg: A }) => await mutator(callApi, arg as A)
  );
  const toast = useToast();

  const run = async (arg?: A) => {
    try {
      return await (trigger as (a?: A) => Promise<T>)(arg as A);
    } catch (e) {
      if (!suppressToast) showToast(toast, ToastFactory.fromError(e));
      onError?.(e);
      throw e;
    }
  };

  return run as unknown as (arg: A) => Promise<T>;
}

export function useApiClient() {
  const { accounts, instance } = useMsal();

  const authenticatedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      let token = import.meta.env.VITE_ACCESS_TOKEN as string | undefined;

      if (!token) {
        const msalRequest = {
          account: accounts[0],
          scopes: [`api://${import.meta.env.VITE_CLIENT_ID}/user_impersonation`],
        };

        try {
          const result = await instance.acquireTokenSilent(msalRequest);
          token = result.accessToken;
        } catch (error) {
          if (
            error instanceof InteractionRequiredAuthError ||
            (error instanceof BrowserAuthError && error.errorCode === 'timed_out')
          ) {
            await instance.acquireTokenRedirect(msalRequest);
            return new Promise(() => {});
          }
          throw error;
        }
      }

      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);

      return fetch(input, { ...init, headers });
    },
    [accounts, instance]
  );

  return useMemo(
    () => hc<AppType>(import.meta.env.VITE_API_BASE_URL ?? '', { fetch: authenticatedFetch }),
    [authenticatedFetch]
  );
}

export type ApiClient = ReturnType<typeof useApiClient>;

const RETRYABLE_STATUSES = new Set([500, 502, 503, 504, 522, 524, 408]);
const RETRY_COUNT = 3;
const RETRY_BASE_DELAY_MS = 500;

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    throw new ApiError((body as { detail?: string }).detail ?? response.statusText, {
      response: {
        data: body,
        status: response.status,
        statusText: response.statusText,
        headers,
      },
    });
  }
  if (response.status === 204) return undefined as unknown as T;
  return (await response.json()) as T;
};

const isRetryable = (e: unknown, attempt: number): boolean => {
  const status = (e as { status?: number }).status;
  return attempt < RETRY_COUNT && RETRYABLE_STATUSES.has(status ?? 0);
};

const fetchWithRetry = async <T>(doFetch: () => Promise<Response>): Promise<T> => {
  let attempt = 0;
  let lastError: unknown;
  while (attempt <= RETRY_COUNT) {
    try {
      return await parseResponse<T>(await doFetch());
    } catch (e) {
      lastError = e;
      if (isRetryable(e, attempt)) {
        const status = (e as { status?: number }).status;
        console.warn(
          `API fetch failed with status ${status}. Retrying (${RETRY_COUNT - attempt} retries left)...`
        );
        const delay = RETRY_BASE_DELAY_MS * 2 ** attempt;
        await new Promise((res) => setTimeout(res, delay));
        attempt += 1;
      } else {
        throw e;
      }
    }
  }
  throw lastError;
};

/** High-level read hook using the Hono `hc` client. SWR-backed with retries and error toasts. */
export function useFetch<T>(
  key: Key | null,
  fetcher: (client: ApiClient) => Promise<Response>,
  onErrorOrOptions?: ((error?: unknown) => void) | ReadToastOptions,
  swrOptions?: SWRConfiguration<T>
) {
  const client = useApiClient();
  const toast = useToast();
  const onError = typeof onErrorOrOptions === 'function' ? onErrorOrOptions : undefined;
  const showForbiddenErrorToast =
    typeof onErrorOrOptions === 'function'
      ? true
      : (onErrorOrOptions?.showForbiddenErrorToast ?? true);

  const state = useSWR<T>(key, () => fetchWithRetry<T>(() => fetcher(client)), swrOptions);

  const previousErrorRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (key === null || state.isLoading) return;
    if (state.error && state.error !== previousErrorRef.current) {
      previousErrorRef.current = state.error;
      if (showForbiddenErrorToast || !isApiError(state.error) || state.error.status !== 403) {
        showToast(toast, ToastFactory.fromError(state.error));
      }
      onError?.(state.error);
    } else if (state.data === undefined && !state.error && !state.isLoading) {
      const unexpectedError = new UnexpectedError(
        'Data was undefined though no error has been thrown.'
      );
      showToast(toast, ToastFactory.fromError(unexpectedError));
      onError?.(unexpectedError);
    }
  }, [state.error, state.data, state.isLoading, key, toast, onError, showForbiddenErrorToast]);

  return state;
}

/** High-level write hook using the Hono `hc` client. Toasts on error and rejects the mutation. */
export function useMutation<T, A = void>(
  key: Key,
  mutator: (client: ApiClient, arg: A) => Promise<Response>,
  onError?: (error?: unknown) => void,
  options?: { suppressToast?: boolean }
) {
  const client = useApiClient();
  const { trigger } = useSWRMutation<T, unknown, Key, A>(
    key,
    async (_key: Key, { arg }: { arg: A }) => {
      return await parseResponse<T>(await mutator(client, arg));
    }
  );
  const toast = useToast();

  const run = async (arg?: A) => {
    try {
      return await (trigger as (a?: A) => Promise<T>)(arg as A);
    } catch (e) {
      if (!options?.suppressToast) showToast(toast, ToastFactory.fromError(e));
      onError?.(e);
      throw e;
    }
  };

  return run as unknown as (arg: A) => Promise<T>;
}
